/**
 * ApiError — typed error thrown by the api client.
 *
 * Mirrors the backend's error envelope: { code, detail, errors }.
 * UI code branches on `.code`; field-level form errors live in `.errors`.
 *
 * The explicit setPrototypeOf is required because `extends Error` does not
 * always preserve the prototype chain across tools (esbuild / vite / jest).
 */

/**
 * Friendly wording for codes the backend can return on any workspace. The
 * constructor swaps these in for the raw detail, so every `err.message`
 * shown in a toast or note reads well. `feature_off` (404 or 403) is a safety
 * net: routes for switched-off features are already hidden.
 */
const FRIENDLY_MESSAGES: Readonly<Record<string, string>> = {
  feature_off: "That isn't switched on for this workspace.",
  signup_disabled: "Accounts here are created by invitation. Ask your admin to send you an invite.",
  ai_limit_reached: "This workspace has used its AI allowance for the month. It resets next month.",
};

export class ApiError extends Error {
  code: string;
  status?: number;
  errors?: Record<string, string[]>;
  /** The rest of the error body, e.g. `limit` and `active` on `student_limit_reached`. */
  extra: Record<string, unknown>;

  constructor(
    code: string,
    message: string,
    status?: number,
    errors?: Record<string, string[]>,
    extra: Record<string, unknown> = {},
  ) {
    super(FRIENDLY_MESSAGES[code] ?? message);
    Object.setPrototypeOf(this, ApiError.prototype);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.errors = errors;
    this.extra = extra;
  }

  /** True when the server returned 4xx (excluding 401/403). */
  isClientError(): boolean {
    return (
      this.status != null &&
      this.status >= 400 &&
      this.status < 500 &&
      this.status !== 401 &&
      this.status !== 403
    );
  }

  /** True when the server returned 5xx. */
  isServerError(): boolean {
    return this.status != null && this.status >= 500;
  }
}
